// Status utility functions for HR ERP system

/**
 * Get status badge styling classes
 * @param {string} status - Status value
 * @param {string} type - Status type (employee, salary, appraisal, etc.)
 * @returns {string} CSS classes for badge styling
 */
export function getStatusBadgeClasses(status, type = 'default') {
  const statusStyles = {
    employee: {
      active: 'bg-success-100 text-success-800',
      inactive: 'bg-warning-100 text-warning-800',
      terminated: 'bg-danger-100 text-danger-800',
      suspended: 'bg-gray-100 text-gray-800'
    },
    salary: {
      active: 'bg-success-100 text-success-800',
      inactive: 'bg-gray-100 text-gray-800'
    },
    appraisal: {
      completed: 'bg-success-100 text-success-800',
      in_progress: 'bg-warning-100 text-warning-800',
      pending: 'bg-info-100 text-info-800',
      overdue: 'bg-danger-100 text-danger-800'
    },
    // A document's review status, and a checklist category's ('missing': nothing filed).
    document: {
      pending: 'bg-warning-100 text-warning-800',
      approved: 'bg-success-100 text-success-800',
      rejected: 'bg-danger-100 text-danger-800',
      missing: 'bg-gray-100 text-gray-700'
    },
    leave: {
      pending: 'bg-warning-100 text-warning-800',
      approved: 'bg-success-100 text-success-800',
      rejected: 'bg-danger-100 text-danger-800',
      cancelled: 'bg-gray-100 text-gray-800'
    },
    amendment: {
      pending: 'bg-warning-100 text-warning-800',
      acknowledged: 'bg-success-100 text-success-800'
    },
    default: {
      active: 'bg-success-100 text-success-800',
      inactive: 'bg-gray-100 text-gray-800',
      pending: 'bg-warning-100 text-warning-800',
      completed: 'bg-success-100 text-success-800',
      cancelled: 'bg-danger-100 text-danger-800'
    }
  };

  const styles = statusStyles[type] || statusStyles.default;
  return styles[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Format status text for display
 * @param {string} status - Status value
 * @returns {string} Formatted status text
 */
export function formatStatusText(status) {
  if (!status) return '';
  
  // Replace underscores with spaces and capitalize
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get status icon based on status value
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {string} Icon name
 */
export function getStatusIcon(status, type = 'default') {
  const iconMap = {
    employee: {
      active: 'CheckCircle',
      inactive: 'PauseCircle',
      terminated: 'XCircle',
      suspended: 'Clock'
    },
    salary: {
      active: 'CheckCircle',
      inactive: 'XCircle'
    },
    appraisal: {
      completed: 'CheckCircle',
      in_progress: 'Clock',
      pending: 'Calendar',
      overdue: 'AlertCircle'
    },
    document: {
      active: 'FileText',
      archived: 'Archive',
      expired: 'AlertTriangle'
    },
    default: {
      active: 'CheckCircle',
      inactive: 'XCircle',
      pending: 'Clock',
      completed: 'CheckCircle',
      cancelled: 'XCircle'
    }
  };

  const icons = iconMap[type] || iconMap.default;
  return icons[status] || 'Circle';
}

/**
 * Get status color for charts and visualizations
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {string} Color value (hex or Tailwind class)
 */
export function getStatusColor(status, type = 'default') {
  const colorMap = {
    employee: {
      active: '#10B981', // success-500
      inactive: '#F59E0B', // warning-500
      terminated: '#EF4444', // danger-500
      suspended: '#6B7280' // gray-500
    },
    salary: {
      active: '#10B981', // success-500
      inactive: '#6B7280' // gray-500
    },
    appraisal: {
      completed: '#10B981', // success-500
      in_progress: '#F59E0B', // warning-500
      pending: '#3B82F6', // info-500
      overdue: '#EF4444' // danger-500
    },
    document: {
      active: '#10B981', // success-500
      archived: '#6B7280', // gray-500
      expired: '#F59E0B' // warning-500
    },
    default: {
      active: '#10B981', // success-500
      inactive: '#6B7280', // gray-500
      pending: '#F59E0B', // warning-500
      completed: '#10B981', // success-500
      cancelled: '#EF4444' // danger-500
    }
  };

  const colors = colorMap[type] || colorMap.default;
  return colors[status] || '#6B7280'; // gray-500 as default
}

/**
 * Check if status indicates success/positive state
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {boolean} True if status is positive
 */
export function isPositiveStatus(status, type = 'default') {
  const positiveStatuses = {
    employee: ['active'],
    salary: ['active'],
    appraisal: ['completed'],
    document: ['active'],
    default: ['active', 'completed']
  };

  const positive = positiveStatuses[type] || positiveStatuses.default;
  return positive.includes(status);
}

/**
 * Check if status indicates warning/attention needed
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {boolean} True if status needs attention
 */
export function isWarningStatus(status, type = 'default') {
  const warningStatuses = {
    employee: ['inactive', 'suspended'],
    salary: ['inactive'],
    appraisal: ['in_progress', 'pending', 'overdue'],
    document: ['expired'],
    default: ['pending', 'inactive']
  };

  const warnings = warningStatuses[type] || warningStatuses.default;
  return warnings.includes(status);
}

/**
 * Check if status indicates error/negative state
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {boolean} True if status is negative
 */
export function isErrorStatus(status, type = 'default') {
  const errorStatuses = {
    employee: ['terminated'],
    salary: [],
    appraisal: ['overdue'],
    document: [],
    default: ['cancelled', 'terminated']
  };

  const errors = errorStatuses[type] || errorStatuses.default;
  return errors.includes(status);
}

/**
 * Get status priority for sorting
 * @param {string} status - Status value
 * @param {string} type - Status type
 * @returns {number} Priority number (lower = higher priority)
 */
export function getStatusPriority(status, type = 'default') {
  const priorities = {
    employee: {
      active: 1,
      inactive: 2,
      suspended: 3,
      terminated: 4
    },
    salary: {
      active: 1,
      inactive: 2
    },
    appraisal: {
      overdue: 1,
      pending: 2,
      in_progress: 3,
      completed: 4
    },
    document: {
      active: 1,
      expired: 2,
      archived: 3
    },
    default: {
      active: 1,
      pending: 2,
      completed: 3,
      inactive: 4,
      cancelled: 5
    }
  };

  const statusPriorities = priorities[type] || priorities.default;
  return statusPriorities[status] || 999;
} 